import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './css/menu.css';

const headerMessages = [
  "Pretty Tomato",
  "Good Tomato",
  "ajtwoddl Tomato",
  "Stylish Tomato",
  "Handsome Tomato",
  "Smart Tomato",
  "Lovely Tomato",
  "Powerful Tomato",
  "Cute Tomato",
  "Happy Tomato",
  "Crazy Tomato",
  "Serious Tomato",
  "Charming Tomato",
  "Legendary Tomato",
  "Dancing Tomato",
  "Coding Tomato",
  "Shy Tomato",
  "Cool Tomato",
  "Magic Tomato",
  "Mystery Tomato"
];

const Menu = () => {
  const navigate = useNavigate();

  // 랜덤 메시지 상태, 처음 렌더링 시 한 번만 랜덤 선택
  const [headerText, setHeaderText] = useState(headerMessages[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [sortOrder, setSortOrder] = useState('recommended');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * headerMessages.length);
    setHeaderText(headerMessages[randomIndex]);
  }, []); // 빈 배열: 컴포넌트 마운트 시 한 번 실행

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      if (event.key === '/' && !['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  const languages = [
    
    {
      id: 'c',
      title: 'C 언어',
      icon: '/logos/c.png',
      description: 'C 언어는 시스템 프로그래밍과 임베디드 시스템에서 널리 사용되는 강력한 저수준 언어입니다. 효율적인 메모리 관리와 빠른 실행 속도를 제공합니다.',
      difficulty: 2,
      route: '/c/variable',
      recommended: true,
      categories: ['초급', '중급', '고급']
    },
    {
      id: 'csharp',
      title: 'C#',
      icon: '/logos/csharp.svg',
      description: 'C 언어의 변수·조건문·반복문 흐름을 이어서 배열, 메서드, 클래스, 컬렉션, LINQ와 비동기 프로그래밍까지 익힙니다.',
      difficulty: 2,
      route: '/csharp/intro',
      recommended: false,
      categories: ['초급', '객체지향', '.NET']
    },
    {
      id: 'cpp',
      title: 'C++',
      icon: 'https://upload.wikimedia.org/wikipedia/commons/1/18/ISO_C%2B%2B_Logo.svg',
      description: 'C 언어의 기본 문법에서 출발해 참조자, 클래스, 상속, STL 컨테이너까지 C++의 핵심을 실습으로 익힙니다.',
      difficulty: 2,
      route: '/cpp/intro',
      recommended: false,
      categories: ['초급', '객체지향', 'STL']
    },
    {
      id: 'java',
      title: 'Java',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/java/java-original.svg',
      description: 'C 언어와 비슷한 기본 문법부터 클래스, 상속, 인터페이스, 컬렉션과 예외 처리까지 Java의 핵심을 실습으로 익힙니다.',
      difficulty: 2,
      route: '/java/intro',
      recommended: false,
      categories: ['초급', '객체지향', '백엔드']
    },
    {
      id: 'html',
      title: 'HTML',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg',
      description: '웹 문서의 기본 구조부터 텍스트, 링크, 이미지, 표, 폼과 시맨틱 태그까지 직접 페이지를 만들며 익힙니다.',
      difficulty: 1,
      route: '/html/intro',
      recommended: false,
      categories: ['웹', '초급', '프론트엔드']
    },
    {
      id: 'css',
      title: 'CSS',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg',
      description: '선택자와 박스 모델부터 Flexbox, Grid, 반응형 디자인과 애니메이션까지 웹페이지 스타일링의 핵심을 익힙니다.',
      difficulty: 1,
      route: '/css/intro',
      recommended: false,
      categories: ['웹', '디자인', '프론트엔드']
    },
    {
      id: 'javascript',
      title: 'JavaScript',
      icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg',
      description: '기본 문법부터 DOM, 이벤트, 비동기 통신과 브라우저 저장소까지 동작하는 웹페이지를 만들며 익힙니다.',
      difficulty: 2,
      route: '/javascript/intro',
      recommended: false,
      categories: ['웹', '프로그래밍', '프론트엔드']
    },
    {
      id: 'system',
      title: '시스템 프로그래밍',
      icon: '/logos/system.svg',
      description: '운영체제와 하드웨어 가까이에서 동작하는 프로그램을 이해합니다.',
      difficulty: 3,
      route: '/system/complete',
      recommended: false,
      categories: ['중급', '실습', 'OS']
    },
    {
      id: 'logic',
      title: '디지털 논리회로',
      icon: '/logos/logic.svg',
      description: '0과 1, 논리 게이트, 진리표, 조합 회로와 순차 회로의 기본 원리를 차근차근 이해합니다.',
      difficulty: 2,
      route: '/logic/digital-logic-circuit',
      recommended: false,
      categories: ['기초', '회로', '컴퓨터 구조']
    },

    {
      id: 'linux',
      title: '리눅스',
      icon: '/logos/linux.png',
      description: '터미널 기초부터 파일 관리, 권한, 프로세스까지 실습 중심으로 빠르게 익힙니다.',
      difficulty: 2,
      route: '/linux/intro',
      recommended: false,
      categories: ['초급', '기초', '실습']
    },
    {
      id: 'python',
      title: '파이썬',
      icon: '/logos/python.png',
      description: '읽기 쉬운 문법으로 기본 문법부터 함수, 리스트까지 빠르게 익힙니다.',
      difficulty: 1,
      route: '/python/intro',
      recommended: false,
      categories: ['초급', '기초']
    },
    {
      id: 'nextjs',
      title: 'Next.js',
      icon: 'https://nextjs.org/favicon.ico',
      description: 'React를 기반으로 App Router, 서버 컴포넌트, 데이터 처리와 배포까지 현대적인 웹 앱 제작 흐름을 익힙니다.',
      difficulty: 2,
      route: '/nextjs/intro',
      recommended: false,
      categories: ['웹', 'React', '풀스택']
    },
    {
      id: 'git',
      title: 'Git & GitHub',
      icon: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
      description: '버전 관리의 기본부터 저장소, 브랜치, 커밋, Pull Request, 협업 흐름까지 한 번에 익힙니다.',
      difficulty: 2,
      route: '/git/intro',
      recommended: false,
      categories: ['필수', '협업', '실습']
    },
    {
      id: 'firebase',
      title: 'Firebase',
      icon: 'https://firebase.google.com/static/images/brand-guidelines/logo-logomark.png',
      description: 'Firebase 프로젝트 설정부터 Authentication, Firestore, Storage, Functions, Hosting, 보안 규칙까지 익힙니다.',
      difficulty: 3,
      route: '/firebase/intro',
      recommended: false,
      categories: ['백엔드', '클라우드', '실습']
    },
    {
      id: 'vercel',
      title: 'Vercel',
      icon: 'https://assets.vercel.com/image/upload/front/favicon/vercel/180x180.png',
      description: '프로젝트 준비와 Git 연동부터 Preview, 환경 변수, 도메인, Production 배포와 롤백까지 한 번에 익힙니다.',
      difficulty: 2,
      route: '/vercel/intro',
      recommended: false,
      categories: ['배포', '클라우드', '실습']
    },
    {
      id: 'supabase',
      title: 'Supabase',
      icon: 'https://supabase.com/favicon/apple-icon-180x180.png',
      description: '프로젝트 생성과 React 연결부터 Postgres, Auth, RLS, Storage, Realtime, Edge Functions 실행까지 익힙니다.',
      difficulty: 3,
      route: '/supabase/intro',
      recommended: false,
      categories: ['백엔드', 'Postgres', '실습']
    },
    {
      id: 'mysql',
      title: 'MySQL',
      icon: 'https://www.mysql.com/common/logos/logo-mysql-170x115.png',
      description: 'MySQL 설치와 서버 접속부터 SQL CRUD, JOIN, 인덱스, 트랜잭션, 권한과 백업까지 단계별로 익힙니다.',
      difficulty: 2,
      route: '/mysql/intro',
      recommended: false,
      categories: ['데이터베이스', 'SQL', '실습']
    },
    {
      id: 'postgresql',
      title: 'PostgreSQL',
      icon: 'https://www.postgresql.org/media/img/about/press/elephant.png',
      description: 'PostgreSQL 설치와 psql부터 SQL, 관계 설계, 인덱스, 트랜잭션, 역할, 성능 진단과 백업까지 익힙니다.',
      difficulty: 3,
      route: '/postgresql/intro',
      recommended: false,
      categories: ['데이터베이스', 'SQL', '운영']
    }
  ];

  const categories = Array.from(
    new Set(languages.flatMap(language => language.categories))
  ).sort((a, b) => a.localeCompare(b, 'ko'));

  const difficulties = Array.from(
    new Set(languages.map(language => language.difficulty))
  ).sort((a, b) => a - b);

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase('ko-KR');
  const filteredLanguages = languages.filter(language => {
    const searchableText = [
      language.title,
      language.description,
      ...language.categories
    ].join(' ').toLocaleLowerCase('ko-KR');

    const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);
    const matchesCategory = selectedCategory === 'all' || language.categories.includes(selectedCategory);
    const matchesDifficulty = selectedDifficulty === 'all' || language.difficulty === Number(selectedDifficulty);

    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const sortedLanguages = [...filteredLanguages].sort((first, second) => {
    switch (sortOrder) {
      case 'name-asc':
        return first.title.localeCompare(second.title, 'ko', { numeric: true, sensitivity: 'base' });
      case 'name-desc':
        return second.title.localeCompare(first.title, 'ko', { numeric: true, sensitivity: 'base' });
      case 'difficulty-asc':
        return first.difficulty - second.difficulty || first.title.localeCompare(second.title, 'ko');
      case 'difficulty-desc':
        return second.difficulty - first.difficulty || first.title.localeCompare(second.title, 'ko');
      case 'recommended':
        return Number(second.recommended) - Number(first.recommended);
      default:
        return 0;
    }
  });

  const hasActiveFilters = Boolean(normalizedSearch) ||
    selectedCategory !== 'all' ||
    selectedDifficulty !== 'all' ||
    sortOrder !== 'recommended';

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSortOrder('recommended');
    searchInputRef.current?.focus();
  };

  const renderDifficultyDots = (level: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span
        key={i}
        className={`dot ${i < level ? 'active' : ''}`}
      ></span>
    ));
  };

  return (
    <div className="Menu">
      <div className="header animate-fadeInUp">
        <h1>{headerText}</h1>
        <p className="subtitle">배우고 싶은 것을 선택하세요</p>
        <p className="description">
          모든 공부는 직접 따라쓰면서 코드를 하나하나 이해하는 것에서 시작됩니다. <br />
          멋쟁이 토마토와 함께 즐거운 코딩 여행을 떠나보세요!
        </p>
      </div>

      <section className="course-finder animate-fadeInUp" aria-labelledby="course-finder-title">
        <div className="finder-heading">
          <div>
            <h2 id="course-finder-title">강의 빠르게 찾기</h2>
            <p>강의명이나 관심 주제를 입력해 보세요.</p>
          </div>
          <p className="result-count" role="status" aria-live="polite">
            <strong>{filteredLanguages.length}</strong> / {languages.length}개 강의
          </p>
        </div>

        <div className="finder-controls">
          <label className="search-field">
            <span className="search-icon" aria-hidden="true">⌕</span>
            <span className="sr-only">강의 검색</span>
            <input
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="강의명, 주제 검색 (예: C#, SQL, 배포)"
              autoComplete="off"
            />
            <kbd aria-hidden="true">/</kbd>
          </label>

          <label className="filter-field">
            <span>주제</span>
            <select
              aria-label="주제 필터"
              value={selectedCategory}
              onChange={event => setSelectedCategory(event.target.value)}
            >
              <option value="all">모든 주제</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </label>

          <label className="filter-field">
            <span>난이도</span>
            <select
              aria-label="난이도 필터"
              value={selectedDifficulty}
              onChange={event => setSelectedDifficulty(event.target.value)}
            >
              <option value="all">모든 난이도</option>
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>난이도 {difficulty}</option>
              ))}
            </select>
          </label>

          <label className="filter-field">
            <span>정렬</span>
            <select
              aria-label="정렬 순서"
              value={sortOrder}
              onChange={event => setSortOrder(event.target.value)}
            >
              <option value="recommended">추천 순</option>
              <option value="name-asc">이름 오름차순</option>
              <option value="name-desc">이름 내림차순</option>
              <option value="difficulty-asc">쉬운 순</option>
              <option value="difficulty-desc">어려운 순</option>
            </select>
          </label>

          {hasActiveFilters && (
            <button type="button" className="reset-filters" onClick={resetFilters}>
              초기화
            </button>
          )}
        </div>
      </section>

      {filteredLanguages.length > 0 ? (
      <div className="language-grid" aria-label="강의 검색 결과">
        {sortedLanguages.map((lang, index) => (
          <div
            key={lang.id}
            className={`language-card animate-fadeInUp animate-delay-${Math.min(index + 1, 3)}`}
            onClick={() => navigate(lang.route)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate(lang.route);
              }
            }}
            role="link"
            tabIndex={0}
            aria-label={`${lang.title} 강의 열기`}
          >
            {lang.recommended && (
              <div className="recommended-badge">추천!</div>
            )}

            <div className={`language-icon ${lang.id}-icon`}>
              <img src={lang.icon} alt={`${lang.title} 로고`} className="language-logo" />
            </div>

            <h2 className="language-title">{lang.title}</h2>

            <p className="language-description">
              {lang.description}
            </p>

            <div className="learning-info">
              <div className="difficulty">
                <span>난이도:</span>
                <div className="difficulty-dots">
                  {renderDifficultyDots(lang.difficulty)}
                </div>
              </div>
            </div>
            <div className="categories">
              {lang.categories.map(category => (
                <span key={category} className="category-tag">{category}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="empty-results" role="status">
          <div className="empty-icon" aria-hidden="true">🔎</div>
          <h2>조건에 맞는 강의가 없어요</h2>
          <p>검색어를 줄이거나 필터를 초기화해 보세요.</p>
          <button type="button" onClick={resetFilters}>전체 강의 보기</button>
        </div>
      )}
    </div>
  );
};

export default Menu;
